import React, { useState, useEffect } from 'react';
import { Button, Form, Input, Radio, Space } from 'antd';
import Modal from '@/components/Modal/Modal';
import { SearchOutlined } from '@ant-design/icons';
import SelectTeamDialog from '@/pages/MainPage/dialogs/SelectTeamDialog';
import ProjectData from '@/pages/MainPage/datas/ProjectData';
import useHTTP from "@/hooks/useHTTP.mjs";
import runAsync from "@/script/runAsync.mjs";
import { useUser, setUser } from '@/biz/user.mjs';
import { useOpenModal } from '@/components/Modal/index.mjs';
import openWindow from "@/script/openWindow.mjs";
import { isAdmins } from "@/script/admin.js";

const Pane = ({ visible, setVisible, onCall }) => {
    const http = useHTTP();
    const user = useUser(); // 当前用户，需要从App.jsx获取上下文对象以及方法，然后再改变上下文
    const [form] = Form.useForm();
    const openModal = useOpenModal();

    // 数据模型
    const [team, setTeam] = useState({});

    // 加载显示
    const [loading, setLoading] = useState(false);
    const [prjOwner, setPrjOwner] = useState('USER'); // 默认个人
    const [prjType, setPrjType] = useState('NEW');
    const [teamCss, setTeamCss] = useState({ display: 'none' });

    // 组件初始化
    useEffect(() => {
        if (visible) {
            setPrjType('NEW');
            form.resetFields();
            form.setFieldsValue({ prjOwnerTypeCode: 'USER', prjTypeCode: 'NEW', subTypeCode: 'BODA2', desgAcc: 'SF_LVL', llmApiKeyOwnerType: 'PRJ' });

            setPrjOwner('USER');
            setTeamCss({ display: 'none' });
        }
    }, [visible]);

    const doAdd = async () => {
        try {
            setLoading(true);

            // 表单校验
            const formData = await form.validateFields();
            const prjTypeCode = formData.prjTypeCode;
            formData.teamId = team.pkid;

            // 非BODA软件模型 或  非知识模型，需要清空 subTypeCode
            if (prjTypeCode != 'NEW' && prjTypeCode != 'KM') {
                formData.subTypeCode = null;
            }

            // 1、创建项目
            const prj = await runAsync(
                async () => {
                    return await http.post(`/prjs`, formData);
                }, {
                error: { content: (err) => `新建失败: ${err.message}` },
                loading: { show: true },
                success: { show: false },
            });

            // 2、刷新权限
            const {
                projectFaMap,
                projectMenuMap,
                teamFaMap,
                teamMenuMap,
            } = await runAsync(
                async () => {
                    return await http.post(`/perm/refresh`, { pkid: user.pkid });
                }, {
                error: { content: (err) => `刷新权限失败: ${err.message}` },
                loading: { show: true },
                success: { show: true },
            });

            setUser({
                ...user,
                projectFaMap,
                projectMenuMap,
                teamFaMap,
                teamMenuMap,
            });

            setVisible(false);

            // 新建成功后，自动进入项目详情页面。
            openWindow('/ProjectDetail', { prjId: prj.pkid, prjNo: prj.prjNo });

            if (onCall) {
                onCall();
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }

    // 回调，设置团队号信息
    const callTeam = async () => {
        const result = await openModal(<SelectTeamDialog />);
        if (result) {
            setTeam(result);
            form.setFieldsValue({ teamName: result.teamName });
        }
    }

    const onChgOwner = (e) => {
        const owner = e.target.value;
        setPrjOwner(owner);

        if ('USER' == owner) {
            setTeamCss({ display: 'none' });
        } else {
            setTeamCss({ display: 'block' });
            chgTeam();
        }
    }

    const onPrjType = (e) => {
        const prjType = e?.target?.value ?? e;
        setPrjType(prjType);
        if (prjType == 'NEW') {
            form.setFieldsValue({ subTypeCode: 'BODA2' });
        } else {
            form.setFieldsValue({ subTypeCode: null });
        }
    }

    // 切换：团队项目
    const chgTeam = async () => {
        const teamName = user.teamName;

        if (teamName) {
            setTeam({ pkid: user.currPrjTeamId });
            form.setFieldsValue({ teamName });
        } else {
            const data = await runAsync(
                async () => {
                    return await http.get(`/tmusers/${user.pkid}`);
                }, {
                error: { content: (err) => `查询失败: ${err.message}` },
                loading: { show: true },
                success: { show: false },
            });

            if (data.teamName) {
                setTeam({ pkid: data.currPrjTeamId });
                form.setFieldsValue({ teamName: data.teamName });
            }
        }
    }

    return (
        <>
            <Modal
                open={visible}
                title="新建项目"
                okText="保存"
                cancelText="取消"
                onCancel={() => setVisible(false)}
                bodyStyle={{ padding: '12px 24px 2px' }}
                footer={[
                    <Space>
                        <Button onClick={() => setVisible(false)}>取消</Button>
                        <Button type="primary" onClick={doAdd} loading={loading}>保存</Button>
                    </Space>
                ]}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="prjOwnerTypeCode">
                        <Radio.Group onChange={onChgOwner} value={prjOwner}>
                            <Radio value='USER'>个人项目</Radio>
                            <Radio value='TEAM'>团队项目</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item name="teamName" label="团队名称" rules={[{ required: 'TEAM' == prjOwner, message: '请选择团队' }]} css={teamCss}>
                        <Input placeholder="请选择团队" addonAfter={<SearchOutlined onClick={callTeam} />} readOnly />
                    </Form.Item>

                    <ProjectData.prjNo />
                    <ProjectData.prjName />
                    <ProjectData.prjTypeCode isAdmin={isAdmins(user)} onChange={onPrjType} />

                    {
                        (prjType == 'NEW' || prjType == 'KM') &&
                        <ProjectData.subTypeCode isAdmin={isAdmins(user)} prjType={prjType} disabled={prjType == 'NEW'} />
                    }

                    <Form.Item name="llmApiKeyOwnerType" label='AIKEY的获取方式' rules={[{ required: true, message: '请选择AIKEY的获取方式' }]}>
                        <Radio.Group>
                            <Radio value='PRJ'>项目归属者KEY</Radio>
                            <Radio value='USER'>个人配置KEY</Radio>
                        </Radio.Group>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}

export default Pane;
